// The typed API client (paths generated from the backend's OpenAPI: `npm run gen:api`). Every call carries the
// access token; an expired token is refreshed once (single flight) and the call replayed.
import createClient from "openapi-fetch";
import { sessionFrom, sessionStore, type AccessTokenOut } from "@/shared/auth/session";
import { toApiError } from "./problem";
import type { paths } from "./schema";

const CSRF_COOKIE = "delve_csrf";
const REFRESH = "/api/v1/auth/refresh";

export function readCookie(name: string): string {
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : "";
}

export function csrfHeaders(): Record<string, string> {
  const token = readCookie(CSRF_COOKIE);
  return token ? { "X-CSRF-Token": token } : {};
}

let refreshing: Promise<boolean> | null = null;

/** Trades the refresh cookie for a new access token. False when there is no session to resume. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      if (!readCookie(CSRF_COOKIE)) return false;
      const r = await fetch(REFRESH, { method: "POST", credentials: "same-origin", headers: csrfHeaders() });
      if (!r.ok) {
        // Only the API's refusal ends the session (expired, revoked, CSRF): a 5xx is the API or its tunnel being
        // down (the relay answers 502), and the player stays signed in until it comes back.
        if (r.status < 500) sessionStore.set(null);
        return false;
      }
      sessionStore.set(sessionFrom((await r.json()) as AccessTokenOut));
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function withToken(request: Request): Request {
  const session = sessionStore.get();
  if (!session) return request;
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${session.accessToken}`);
  return new Request(request, { headers });
}

async function authFetch(request: Request): Promise<Response> {
  const replay = request.clone();
  const session = sessionStore.get();
  // Refresh a little before expiry rather than eating a 401.
  if (session && session.expiresAt - Date.now() < 20_000) await refreshSession();
  const response = await fetch(withToken(request));
  if (response.status !== 401 || !sessionStore.get()) return response;
  const body = (await response.clone().json().catch(() => ({}))) as { code?: string };
  if (body.code !== "access_token_expired" || !(await refreshSession())) return response;
  return fetch(withToken(replay));
}

export const api = createClient<paths>({ baseUrl: "", fetch: authFetch, credentials: "same-origin" });

/** openapi-fetch returns { data, error }: turn errors into ApiError so TanStack Query and forms see them. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) throw toApiError(result.error, result.response.status);
  return result.data as T;
}
